import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { InventoryUOMMapping, ProductDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { Button, Image, Input, InputNumber, message } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { formatNumber } from 'src/utils/common';
import { batchEnd, batchStart, updateProduct, updateProductInventory } from 'src/utils/firebase/firebase-short-cut';

import CardItem from 'src/atomic-components/atoms/CardItem/CardItem';
import CardTitle from 'src/atomic-components/atoms/CardItem/CardTitle';
import Label from 'src/atomic-components/atoms/TextItem/Label';
import Value from 'src/atomic-components/atoms/TextItem/Value';
import InventoryProductAddingModal from 'src/atomic-components/organisms/Modals/InventoryProductAddingModal';

const defaultUom: InventoryUOMMapping[ProductInventoryUnit] = {
  ratio: 1,
  cost: 0,
  barcode: null,
};

interface InventoryUnitCardProps {
  inventoryId: string;
  unit: ProductInventoryUnit;
  uom: InventoryUOMMapping[ProductInventoryUnit];
  products?: ProductDoc[];
  onRefresh: () => void;
}

const InventoryUnitCard: FC<InventoryUnitCardProps> = ({ inventoryId, unit, uom, products, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedUom, setEditedUom] = useState<InventoryUOMMapping[ProductInventoryUnit]>(uom);
  // 수정 모드에서는 products가 없을 때 추가만 가능하다.
  const [editedProducts, setEditedProducts] = useState<ProductDoc[]>([]);

  const [addingUom, setAddingUom] = useState(defaultUom);
  const [addProducts, setAddProducts] = useState<ProductDoc[]>([]);

  /**
   * 수정 모드
   */
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleEditSave = useCallback(async () => {
    setLoading(true);
    try {
      if (!editedUom) {
        throw new Error('변환 비율과 매입 원가를 입력해주세요.');
      }
      if (editedProducts.length > 0) {
        batchStart();
        await updateProductInventory(inventoryId, { [`uom.${unit}`]: editedUom }, { bBatch: true });
        for (const product of editedProducts) {
          await updateProduct(product._id, { inventoryId, inventoryUnit: unit }, { bBatch: true });
        }
        await batchEnd();
      } else {
        await updateProductInventory(inventoryId, { [`uom.${unit}`]: editedUom });
      }
      message.success(`${unit} 단위 수정에 성공했습니다.`);
    } catch (error) {
      message.error(`${unit} 단위 수정에 실패했습니다.`);
    } finally {
      setLoading(false);
      setIsEditing(false);
      onRefresh();
      setEditedProducts([]);
    }
  }, [editedProducts, editedUom, inventoryId, onRefresh, unit]);

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedUom(uom);
  };

  const handleEditedProduct = (product: ProductDoc) => {
    if (editedProducts.some((p) => p._id === product._id)) {
      message.error('이미 추가된 상품입니다.');
      return;
    }
    setEditedProducts((prev) => [...prev, product]);
  };

  const handleRemoveEditedProduct = (product: ProductDoc) => {
    setEditedProducts((prev) => prev.filter((p) => p._id !== product._id));
  };

  /**
   * 상품 추가모드
   */
  const handleAdd = () => {
    setIsAdding(true);
    setAddingUom(defaultUom);
    setAddProducts([]);
  };

  const handleAddSave = useCallback(async () => {
    setIsAdding(false);
    try {
      if (!addingUom || addingUom.ratio === 0) {
        throw new Error('변환 비율과 매입 원가를 입력해주세요.');
      }
      batchStart();
      await updateProductInventory(inventoryId, { [`uom.${unit}`]: addingUom }, { bBatch: true });
      for (const product of addProducts) {
        await updateProduct(product._id, { inventoryId, inventoryUnit: unit }, { bBatch: true });
      }
      await batchEnd();
      message.success(`${unit} 단위 추가에 성공했습니다.`);
    } catch (error) {
      message.error(`${unit} 단위 추가에 실패했습니다.`);
    } finally {
      setLoading(false);
      setIsAdding(false);
      onRefresh();
      setAddProducts([]);
    }
  }, [addProducts, addingUom, inventoryId, onRefresh, unit]);

  const handleAddProduct = (product: ProductDoc) => {
    if (addProducts.some((p) => p._id === product._id)) {
      message.error('이미 추가된 상품입니다.');
      return;
    }
    setAddingUom({ ...addingUom, cost: product.cost });
    setAddProducts((prev) => [...prev, product]);
  };

  const handleRemoveProduct = (product: ProductDoc) => {
    setAddProducts((prev) => prev.filter((p) => p._id !== product._id));
  };

  const handleAddCancel = () => {
    setIsAdding(false);
    setAddingUom(defaultUom);
    setAddProducts([]);
  };

  useEffect(() => {
    setEditedUom(uom);
    setAddProducts([]);
    setEditedProducts([]);
  }, [uom]);

  return (
    <>
      {uom && editedUom ? (
        <>
          <CardItem>
            <CardTitle title={unit} />
            {!isEditing ? (
              <Button size='small' onClick={handleEdit}>
                수정
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: 4 }}>
                <Button type='primary' size='small' onClick={handleEditSave} loading={loading}>
                  저장
                </Button>
                <Button size='small' onClick={handleEditCancel} loading={loading}>
                  취소
                </Button>
              </div>
            )}
          </CardItem>
          <CardItem>
            <Label>연결 상품</Label>
            {!products && isEditing && <InventoryProductAddingModal onAdd={handleEditedProduct} />}
          </CardItem>
          {products
            ? products.map((product) => (
                <CardItem key={product._id}>
                  <Image
                    width={40}
                    height={40}
                    src={product.thumbnail ?? undefined}
                    style={{ borderRadius: 4, border: '1px solid var(--gray200)' }}
                  />
                  <Value>{product.fullName}</Value>
                </CardItem>
              ))
            : editedProducts.map((product) => (
                <CardItem key={product._id}>
                  <Image
                    width={40}
                    height={40}
                    src={product.thumbnail ?? undefined}
                    style={{ borderRadius: 4, border: '1px solid var(--gray200)' }}
                  />
                  <Value>{product.fullName}</Value>
                  <Button
                    size='small'
                    icon={<MinusOutlined />}
                    danger
                    onClick={() => handleRemoveEditedProduct(product)}
                    style={{ marginLeft: 6 }}
                  />
                </CardItem>
              ))}
          <CardItem>
            <Label>변환 비율</Label>
            {isEditing ? (
              <InputNumber
                className='flex1'
                value={editedUom.ratio}
                min={1}
                onChange={(value) => setEditedUom({ ...editedUom, ratio: value ?? 0 })}
              />
            ) : (
              <Value>{uom.ratio}</Value>
            )}
          </CardItem>
          <CardItem>
            <Label>매입 원가</Label>
            {isEditing ? (
              <InputNumber
                className='flex1'
                value={editedUom.cost}
                onChange={(value) => setEditedUom({ ...editedUom, cost: value ?? 0 })}
              />
            ) : (
              <Value>{formatNumber(uom.cost)}</Value>
            )}
          </CardItem>
          <CardItem>
            <Label>재고 바코드</Label>
            {isEditing ? (
              <Input
                className='flex1'
                value={editedUom.barcode ?? ''}
                onChange={(e) => setEditedUom({ ...editedUom, barcode: e.target.value })}
              />
            ) : (
              <Value>{uom.barcode ?? '-'}</Value>
            )}
          </CardItem>
        </>
      ) : (
        <>
          <CardItem>
            <CardTitle title={unit} />
            {!isAdding ? (
              <Button size='small' icon={<PlusOutlined />} onClick={handleAdd} loading={loading}>
                등록
              </Button>
            ) : (
              <div style={{ display: 'flex', gap: 4 }}>
                <Button size='small' type='primary' onClick={handleAddSave} loading={loading}>
                  등록
                </Button>
                <Button size='small' onClick={handleAddCancel} loading={loading}>
                  취소
                </Button>
              </div>
            )}
          </CardItem>
          {isAdding && (
            <>
              <CardItem>
                <Label>연결 상품</Label>
                <InventoryProductAddingModal onAdd={handleAddProduct} />
              </CardItem>
              {addProducts?.map((product) => (
                <CardItem key={product._id}>
                  <Image
                    width={40}
                    height={40}
                    src={product.thumbnail ?? undefined}
                    style={{ borderRadius: 4, border: '1px solid var(--gray200)' }}
                  />
                  <Value>{product.fullName}</Value>
                  <Button
                    size='small'
                    icon={<MinusOutlined />}
                    danger
                    onClick={() => handleRemoveProduct(product)}
                    style={{ marginLeft: 6 }}
                  />
                </CardItem>
              ))}
              <CardItem>
                <Label>변환 비율</Label>
                <InputNumber
                  className='flex1'
                  value={addingUom.ratio}
                  min={1}
                  onChange={(value) => setAddingUom({ ...addingUom, ratio: value ?? 0 })}
                />
              </CardItem>
              <CardItem>
                <Label>매입 원가</Label>
                <InputNumber
                  className='flex1'
                  value={addingUom.cost}
                  onChange={(value) => setAddingUom({ ...addingUom, cost: value ?? 0 })}
                />
              </CardItem>
              <CardItem>
                <Label>재고 바코드</Label>
                <Input
                  className='flex1'
                  value={addingUom.barcode ?? ''}
                  onChange={(e) => setAddingUom({ ...addingUom, barcode: e.target.value })}
                />
              </CardItem>
            </>
          )}
        </>
      )}
    </>
  );
};

export default InventoryUnitCard;
